<template>
  <div class="md:container md:mx-auto">
    <Head title="Edit Project" />
    <h1 class="text-3xl font-bold">
      <Link class="text-blue-400 hover:text-blue-600" href="/projects">Projects</Link>
      <span class="text-blue-400 font-medium">/</span>
      Edit Project
    </h1>
    <trashed-message v-if="project.deleted_at" class="mb-6" @restore="restore"> This project has been deleted. </trashed-message>
    <form @submit.prevent="update">
      <div class="md:flex">
        <!-- Project -->
        <div class="w-full md:w-2/5 p-2">
          <h2 class="pb-8 italic text-xl">Project</h2>

          <div class="flex flex-wrap mb-2 w-full">
            <text-input v-model="form.title" name="title" :error="form.errors.title" class="pb-8 pr-6 w-full max-w-sm" label="Project Title *" placeholder="Project Title" tabindex="1" required />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.address" name="address" :error="form.errors.address" class="pb-8 pr-6 w-full max-w-sm" label="Project Address" placeholder="Address" tabindex="2" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.city" name="city" :error="form.errors.city" class="pb-8 pr-6 w-full max-w-sm" label="City" placeholder="City" tabindex="3" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.state" name="state" :error="form.errors.state" class="pb-8 pr-6 w-full max-w-sm" label="State/Region" placeholder="State/Region" tabindex="4" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.country" name="country" :error="form.errors.country" class="pb-8 pr-6 w-full max-w-sm" label="Country" placeholder="Select Country" tabindex="5" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.zipcode" name="zipcode" :error="form.errors.zipcode" class="pb-8 pr-6 w-full max-w-sm" label="Zip/Post Code" placeholder="Zip/Post Code" tabindex="6" />
          </div>
          <div v-if="auth.user.is_admin" class="flex flex-wrap mb-2">
            <select-input v-model="form.price_list_id" :error="form.errors.price_list_id" class="pb-8 pr-6 w-full max-w-sm" label="Price List" tabindex="7">
              <option v-for="pl in pricelists" :key="pl.id" :value="pl.id"> {{ pl.name }}</option>
            </select-input>
          </div>
        </div>

        <!-- Customer -->
        <div class="w-full md:w-3/5 p-2 pt-10 md:pt-2 md:pl-24 border-t md:border-t-0 md:border-l border-gray">
          <h2 class="pb-8 italic text-xl">Customer</h2>

          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.company" name="company" :error="form.errors.company" class="pb-8 pr-6 w-full max-w-sm" label="Company *" placeholder="Company name" tabindex="8" required />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.contact" name="contact" :error="form.errors.contact" class="pb-8 pr-6 w-full max-w-sm" label="Contact *" placeholder="contact name" tabindex="9" required />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.email" name="email" :error="form.errors.email" class="pb-8 pr-6 w-full max-w-sm" type="email" label="Email *" placeholder="Email Address" tabindex="10" required />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.phone" name="phone" :error="form.errors.phone" class="pb-8 pr-6 w-full max-w-sm" type="tel" mask="(###) ###-####" label="Phone" placeholder="Phone number" tabindex="11" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.branch_location" name="branch_location" :error="form.errors.branch_location" class="pb-8 pr-6 w-full max-w-sm" label="Branch Location *" placeholder="Branch Location" tabindex="12" required />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.company_city" name="company_city" :error="form.errors.company_city" class="pb-8 pr-6 w-full max-w-sm" label="City" placeholder="Company City" tabindex="12" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.company_state" name="company_state" :error="form.errors.company_state" class="pb-8 pr-6 w-full max-w-sm" label="State" placeholder="Company State" tabindex="13" />
          </div>
        </div>
      </div>
      <div class="flex items-center px-8 pl-0 py-4 bg-gray-50 border-t border-gray-100">
        <button v-if="! project.deleted_at" class="text-red-600 hover:underline ml-4" type="button" @click="destroy">Delete Project</button>
        <loading-button :loading="form.processing" class="btn-blue ml-auto" tabindex="-1" type="submit">Update Project</loading-button>
      </div>
    </form>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'

export default {
  components: {
    Head,
    Link,
    TextInput,
    SelectInput,
    LoadingButton,
    TrashedMessage,
  },
  layout: Layout,
  props: {
    auth: Object,
    project: Object,
    pricelists: Array,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        title          : this.project.title,
        address        : this.project.address,
        city           : this.project.city,
        state          : this.project.state,
        country        : this.project.country,
        zipcode        : this.project.zipcode,
        price_list_id  : this.project.price_list_id,
        company        : this.project.company,
        contact        : this.project.contact,
        email          : this.project.email,
        phone          : this.project.phone,
        branch_location: this.project.branch_location,
        company_city   : this.project.company_city,
        company_state  : this.project.company_state,
      }),
    }
  },
  methods: {
    update() {
      this.form.put(`/projects/${this.project.id}`)
    },
    destroy() {
      if (confirm('Are you sure you want to delete this project?')) {
        this.$inertia.delete(`/projects/${this.project.id}`)
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this project?')) {
        this.$inertia.put(`/projects/${this.project.id}/restore`)
      }
    },
  },
}
</script>
